import type { VotingQuestionWithResults } from '../../api/DigiobcanApi';
import { scalePoints } from '../../lib/votings';
import { __ } from '../../utils';
import { Box, Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type ScaleOption = VotingQuestionWithResults['options'][number];

const bucketCount = (option: ScaleOption, point: number) =>
  option.histogram?.find((bucket) => bucket.value === point)?.count ?? 0;

/** `null` znamená „nikto neohodnotil", nie nulu - preto pomlčka, nie 0,0. */
const averageLabel = (option: ScaleOption) =>
  option.average === null || option.average === undefined
    ? '-'
    : option.average.toFixed(1).replace('.', ',');

type DistributionProps = {
  option: ScaleOption;
  points: number[];
  /** Výška najvyššieho stĺpca v px. */
  height: number;
  barWidth: number;
};

/**
 * Rozdelenie hodnotení ako miniatúrny stĺpcový graf. Stĺpce sú škálované na
 * najvyšší **v rámci tvrdenia**, nie naprieč tabuľkou: porovnáva sa tvar
 * odpovede na jedno tvrdenie, nie počty medzi tvrdeniami - tie sú v číslach
 * vedľa.
 */
const Distribution: React.FC<DistributionProps> = ({
  option,
  points,
  height,
  barWidth,
}) => {
  const counts = points.map((point) => bucketCount(option, point));
  const max = Math.max(...counts, 1);

  return (
    <Stack
      direction='row'
      alignItems='flex-end'
      justifyContent='flex-end'
      gap={0.375}
      sx={{ height }}
      aria-hidden
    >
      {counts.map((count, index) => (
        <Box
          key={points[index]}
          sx={({ palette }) => ({
            width: barWidth,
            // Minimum 3 px, aby aj nula bola linka a nie diera v grafe.
            height: Math.max(3, Math.round((count / max) * height)),
            borderRadius: '2px',
            backgroundColor:
              count === max ? palette.blue.dark : palette.blue.surface,
            ...(count > 0 &&
              count !== max && { backgroundColor: palette.blue.light }),
          })}
        />
      ))}
    </Stack>
  );
};

type Props = {
  question: VotingQuestionWithResults;
};

/**
 * Škála pre úradníka: tabuľka s priemerom, počtami v stĺpcoch 1 - N
 * a rozdelením. Zostáva tabuľkou aj po redizajne, lebo je to jediný pohľad,
 * z ktorého sa dá odpísať číslo do zápisnice - a tie čísla sú tie isté, čo
 * v exporte.
 *
 * Kruhový graf by tu bol nezmysel: hodnotia sa **všetky** tvrdenia, takže
 * každý výsek by bol `1/počet tvrdení` bez ohľadu na to, ako ľudia hlasovali.
 */
export const ScaleResultsTable: React.FC<Props> = ({ question }) => {
  const points = scalePoints(question);
  const columns = `minmax(160px, 2.2fr) 72px repeat(${points.length}, 52px) 96px`;

  return (
    <Stack gap={1.25}>
      <Box
        sx={({ palette, shape }) => ({
          border: `1px solid ${palette.divider}`,
          borderRadius: `${shape.borderRadius}px`,
          overflowX: 'auto',
        })}
      >
        <Box sx={{ minWidth: 560 }}>
          <Box
            sx={({ palette }) => ({
              display: 'grid',
              gridTemplateColumns: columns,
              alignItems: 'center',
              height: 40,
              px: 2,
              backgroundColor: palette.grey[100],
              borderBottom: `1px solid ${palette.divider}`,
              fontSize: 12,
              fontWeight: 700,
              color: palette.grey[600],
            })}
          >
            <span>{__('Tvrdenie')}</span>
            <span style={{ textAlign: 'right' }}>{__('Ø')}</span>
            {points.map((point) => (
              <span key={point} style={{ textAlign: 'right' }}>
                {point}
              </span>
            ))}
            <span style={{ textAlign: 'right' }}>{__('Rozdelenie')}</span>
          </Box>

          {question.options.map((option) => (
            <Box
              key={option.option_id}
              sx={({ palette }) => ({
                display: 'grid',
                gridTemplateColumns: columns,
                alignItems: 'center',
                minHeight: 52,
                px: 2,
                borderBottom: `1px solid ${palette.dividerLight}`,
                fontSize: 14,
                '&:last-of-type': { borderBottom: 'none' },
              })}
            >
              <span>{option.title}</span>
              <b style={{ textAlign: 'right', fontWeight: 700 }}>
                {averageLabel(option)}
              </b>
              {points.map((point) => (
                <Typography
                  key={point}
                  component='span'
                  sx={({ typography }) => ({
                    textAlign: 'right',
                    fontFamily: typography.fontFamilyMono,
                    fontSize: 14,
                    color: 'text.secondary',
                  })}
                >
                  {bucketCount(option, point)}
                </Typography>
              ))}
              <Distribution
                option={option}
                points={points}
                height={26}
                barWidth={points.length > 6 ? 8 : 12}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Typography variant='caption' color='text.secondary'>
        {__(
          'Ø je priemer hodnotení; „-" znamená, že tvrdenie nikto neohodnotil. Čísla v stĺpcoch sú počty hlasov, rovnaké ako v exporte.',
        )}
      </Typography>
    </Stack>
  );
};

/**
 * Škála pre občana: tvrdenie, tvar odpovedí a priemer. Bez surových počtov -
 * občan z nich nič nerobí a rozbili by riadok na mobile.
 */
export const ScaleResultsSummary: React.FC<Props> = ({ question }) => {
  const points = scalePoints(question);

  return (
    <Stack gap={2}>
      {question.options.map((option) => (
        <Stack
          key={option.option_id}
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={{ xs: 1, sm: 2 }}
        >
          <Typography sx={{ flex: 1, fontSize: 15 }}>{option.title}</Typography>
          <Stack
            direction='row'
            alignItems='center'
            gap={2}
            sx={{ flex: 'none' }}
          >
            <Distribution
              option={option}
              points={points}
              height={44}
              barWidth={points.length > 6 ? 16 : 26}
            />
            <Typography
              component='b'
              sx={{
                width: 56,
                textAlign: 'right',
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              {`Ø ${averageLabel(option)}`}
            </Typography>
          </Stack>
        </Stack>
      ))}

      <Typography variant='caption' color='text.secondary'>
        {`${__('Stĺpce zľava doprava: hodnotenie')} ${points[0]}${
          question.scale_min_label ? ` (${question.scale_min_label})` : ''
        } ${__('až')} ${points[points.length - 1]}${
          question.scale_max_label ? ` (${question.scale_max_label})` : ''
        }.`}
      </Typography>
    </Stack>
  );
};
